import React, { useState, useContext } from "react";
import { Box, Button, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { API, Auth } from "aws-amplify";
import { learncardsContext } from "../../App";
import { useNavigate } from "react-router-dom";
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import IconButton from '@mui/material/IconButton';
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";



const Sets = () => {
  const { selectedOption, setSelectedOption, setLoading, setCardData, setOptions, options } = useContext(learncardsContext);
  const [newSetName, setNewSetName] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleCreateSetClick = async () => {
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      };
      const response = await API.post("FRONTEND_HTTP_API", "/createSet", {
        body: { name: newSetName },
        headers,
      });
      console.log("Create set successful:", response);

      // Update options with the new set
      const newSet = { name: newSetName, last_modified: new Date().toLocaleString() };
      setOptions([...options, newSet]);
      setSelectedOption(newSetName);
      setNewSetName(""); // Clear the input field after creating a set
      navigate("/cards");
    } catch (error) {
      console.error("Error creating set:", error);
    }
  };

  const handleDeleteSet = async (setName) => {
    if (window.confirm(`Are you sure you want to delete the set "${setName}"?`)) {
      try {
        const session = await Auth.currentSession();
        const accessToken = session.getAccessToken().getJwtToken();
        const headers = {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        };
        await API.del("FRONTEND_HTTP_API", `/deleteSet?name=${setName}`, {
          headers,
        });

        // Update options by removing the deleted set
        setOptions(options.filter(set => set.name !== setName));

        // If the deleted set was selected, clear the selection
        if (selectedOption === setName) {
          setSelectedOption("");
        }
      } catch (error) {
        console.error("Error deleting set:", error);
      }
    }
  };

  const handleArchiveSet = async (setName) => {

    if (window.confirm(`Are you sure you want to archive the set "${setName}"?`)) {
      try {
        const session = await Auth.currentSession();
        const accessToken = session.getAccessToken().getJwtToken();
        const headers = {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        };
        let archive = true; //we want to archive
        if (setName.includes('archived/')) {
          archive = false; //we want to unarchive
        }
        await API.post("FRONTEND_HTTP_API", `/archiveSet?name=${setName}&archive=${archive}`, {
          headers,
        });

        // Update options by marking the set as archived
        setOptions(options.map(set =>
          set.name === setName ? { ...set, archived: true } : set
        ));

        // If the archived set was selected, clear the selection
        if (selectedOption === setName) {
          setSelectedOption("");
        }
      } catch (error) {
        console.error("Error archiving set:", error);
      }
    }
  };

  // Prepare rows from the options available in the context
  const setsData = options.map((set, index) => ({
    id: set.name, // Using set name as the unique identifier
    name: set.name,
    last_modified: set.last_modified, // Using the actual last_modified date
  }));

  const columns = [
    //give column same width depedning on the content
    //remove archived/ from the name
    {
      field: "name",
      headerName: "Naam",
      flex: 1,
      valueGetter: (params) => {
        return params.value.replace('archived/', '');
      },
    },
    // {
    //   field: "last_modified",
    //   headerName: "Datum van laatste aanpassing",
    //   flex: 1,
    //   valueGetter: (params) => {
    //     if (!params.value) return '';
    //     const date = new Date(params.value);
    //     return date.toLocaleString('nl-NL', {
    //       year: 'numeric',
    //       month: 'long',
    //       day: 'numeric',
    //       hour: '2-digit',
    //       minute: '2-digit',
    //       timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    //     });
    //   }
    //},
    {
      field: 'archive',
      headerName: 'Archiveren',
      flex: 1,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleArchiveSet(params.row.name);
          }}
        >

          {/*
          //if name contains archived/ then show icon to unarchive
          //else show icon to archive
          */}
          {params.row.name.includes('archived/') ?
            <UnarchiveIcon color='info' />
            :
            <ArchiveIcon color='primary' > </ArchiveIcon>
          }
        </IconButton>
      ),
    },
    {
      field: 'delete',
      headerName: 'Verwijderen',
      flex: 1,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteSet(params.row.name);
          }}
        >
          <DeleteIcon color="error" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box m="20px">
      {/* New input box for adding a new set 
      <Box display="flex" alignItems="center" mb="20px">
        <TextField
          label="Naam nieuwe verzameling"
          value={newSetName}
          onChange={(e) => setNewSetName(e.target.value)}
          sx={{ width: '300px', mr: 2 }} // Adjust width as needed
        />
        <Button variant="contained" onClick={handleCreateSetClick}>
          Maak nieuwe verzameling
        </Button>
      </Box>
      */}

      <DataGrid
        rows={setsData}
        columns={columns}
        autoHeight
        disableSelectionOnClick
        sx={{
          '& .MuiDataGrid-main': { width: '100%' },
          '& .MuiDataGrid-cell': { maxWidth: 'unset !important' },
          '& .MuiDataGrid-columnHeaders': {
            maxWidth: 'unset !important',
            backgroundColor: 'rgb(223, 84, 42,0.7)',
            color: 'black',
            //fontSize: '1.0rem',
            //make font super bold with extra font weight
            fontWeight: 'bold',
            fontSize: '1.0rem',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: `1px solid ${colors.primary[200]}`,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            padding: '0 16px',
          },
        }}
      />
    </Box>
  );
};

export default Sets;
